import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'

import {
  mergeReducedMotionPreset,
  resolveExpandTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import { useTheme } from '../../theme/ThemeContext'
import { AgentStatusDotSlot } from './AgentStatusDot'

export type AgentTodoItem = {
  id: string
  content: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
}

type AgentTodoFoldProps = {
  items: AgentTodoItem[]
  collapsedByDefault?: boolean
  forceOpen?: boolean
  /** 外部持久化展开状态（页面恢复时使用） */
  externalOpen?: boolean
  /** 用户手动展开/收起回调（用于持久化） */
  onToggle?: (open: boolean) => void
}

/**
 * 执行计划面板：
 * - 整卡片和每个步骤默认全部展开，执行历史永久可见
 * - 用户可手动收起/展开，状态变化不强制覆盖手动操作
 */
export function AgentTodoFold({
  items,
  collapsedByDefault = false,
  forceOpen = false,
  externalOpen,
  onToggle,
}: AgentTodoFoldProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const expandTrans = resolveExpandTransition(
    mergeReducedMotionPreset(motionPreset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )

  const completed = useMemo(
    () => items.filter((item) => item.status === 'completed').length,
    [items],
  )
  const total = items.length
  const activeIdx = useMemo(
    () => items.findIndex((item) => item.status === 'in_progress'),
    [items],
  )

  /** 整卡片默认展开，永不自动收起；外部持久化态优先（跨页面恢复） */
  const initialOpen = externalOpen !== undefined ? externalOpen : (forceOpen || !collapsedByDefault)
  const [open, setOpen] = useState(initialOpen)

  // externalOpen 变化时同步
  useEffect(() => {
    if (externalOpen !== undefined) setOpen(externalOpen)
  }, [externalOpen])

  if (total === 0) return null

  const summary = `${completed} / ${total} 计划完成`

  return (
    <div
      className={[
        'agent-todo-fold min-w-0 overflow-hidden',
        angular
          ? 'rounded-none border border-line bg-panel/80'
          : 'rounded-xl border border-[color-mix(in_srgb,var(--line)_55%,transparent)] bg-[color-mix(in_srgb,var(--panel)_88%,var(--surface-muted)_12%)]',
      ].join(' ')}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] font-medium text-ink transition hover:bg-surface-muted/40"
        onClick={() => {
          const next = !open
          setOpen(next)
          onToggle?.(next)
        }}
        aria-expanded={open}
      >
        <motion.svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          className="shrink-0 text-ink-soft"
          animate={{ rotate: open ? 0 : -90 }}
          transition={expandTrans}
          aria-hidden
        >
          <path d="m6 9 6 6 6-6" />
        </motion.svg>
        <span className="min-w-0 flex-1 truncate">{summary}</span>
        {activeIdx >= 0 ? (
          <span className="shrink-0 text-[11px] text-ink-soft">执行中</span>
        ) : null}
      </button>

      {reducedMotion ? (
        open ? (
          <ul className="space-y-0.5 border-t border-[color-mix(in_srgb,var(--line)_28%,transparent)] px-3 py-2">
            {items.map((item) => (
              <TodoRow key={item.id} item={item} expandTrans={expandTrans} />
            ))}
          </ul>
        ) : null
      ) : (
        <AnimatePresence initial={false}>
          {open ? (
            <motion.div
              key="todo-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={expandTrans}
              className="overflow-hidden"
            >
              <ul className="space-y-0.5 border-t border-[color-mix(in_srgb,var(--line)_28%,transparent)] px-3 py-2">
                {items.map((item) => (
                  <TodoRow key={item.id} item={item} expandTrans={expandTrans} />
                ))}
              </ul>
            </motion.div>
          ) : null}
        </AnimatePresence>
      )}
    </div>
  )
}

function TodoRow({
  item,
}: {
  item: AgentTodoItem
  expandTrans?: ReturnType<typeof resolveExpandTransition>
}) {
  const active = item.status === 'in_progress'
  const done = item.status === 'completed'
  const cancelled = item.status === 'cancelled'

  const status = done
    ? 'completed'
    : active
      ? 'running'
      : cancelled
        ? 'cancelled'
        : 'pending'

  return (
    <li className="min-w-0">
      {/* 步骤行是纯展示：不可点击展开，状态由圆点与文本直接表达 */}
      <div className="flex w-full min-w-0 items-center gap-2 py-0.5 text-left text-[12px] leading-5">
        <AgentStatusDotSlot status={status} size="sm" />
        <span
          className={[
            'min-w-0 flex-1 break-words',
            done || cancelled
              ? 'text-ink-soft line-through'
              : active
                ? 'text-ink font-medium'
                : 'text-ink-soft',
          ].join(' ')}
        >
          {item.content}
        </span>
      </div>
      {active ? (
        <p className="pl-5 pb-1 text-[11px] leading-4 text-ink-soft/85">正在执行此步骤…</p>
      ) : null}
    </li>
  )
}
